import {
  forwardRef,
  ComponentPropsWithoutRef,
  ReactElement,
  useId,
  useMemo,
} from "react";
import { stripInlineMarkdown, warnOnce } from "../../internal/functions";
import styles from "./Checkbox.module.scss";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { FieldLabel } from "../FieldLabel";
import type { FieldLabelProps as FieldLabelElementProps } from "../FieldLabel";
import { FieldLabelProps as InternalFieldLabelProps } from "../../internal/types";
import { HelperProps } from "../../internal/components";
import FieldMessage, { warnDeprecatedErrorUsage } from "../FieldMessage";
import { CheckboxGroupContext } from "./internal/CheckboxGroupContext";

/**
 * Props for the CheckboxGroup component
 * @extends ComponentPropsWithoutRef<"fieldset">
 * @extends LayoutUtilProps
 * @extends Omit<InternalFieldLabelProps, "labelProps">
 */
export type CheckboxGroupProps = ComponentPropsWithoutRef<"fieldset"> &
  LayoutUtilProps &
  Omit<InternalFieldLabelProps, "labelProps"> & {
    /**
     * required flag for label
     * @accessibility This does not enforce error when nothing is selected.
     * @default false
     */
    required?: boolean;

    // TODO(v4.0.0): type becomes string only
    /**
     * Legend for the fieldset.
     * Passing `ReactNode` is deprecated — use a plain string with inline markdown instead.
     */
    legend: string | ReactElement;

    /**
     * Additional props for the legend `FieldLabel` (for example `aiMark`, `className`).
     */
    legendProps?: Omit<FieldLabelElementProps, "el">;

    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     */
    error?: boolean | string | ReactElement | string[];
    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];
    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];
    /**
     * Description text to display below the field.
     */
    description?: HelperProps["description"];
    /**
     * Hint text to display below the field.
     */
    hint?: HelperProps["hint"];
    /**
     * When `true`, hides the visual label row. The sr-only `<legend>` always remains for screen readers.
     * @default false
     */
    hideLabel?: boolean;
  };

/**
 * CheckboxGroup component for grouping related checkboxes together.
 *
 * Features:
 * - Groups multiple checkboxes with a common legend
 * - Supports required field indication
 * - Optional helper text and descriptions
 * - Full accessibility support with fieldset/legend
 * - Focus management for keyboard navigation
 * - Layout utility props for positioning and spacing
 * - Flexible layout direction (row/column)
 * - Screen reader support with hidden labels
 *
 * @example
 * <CheckboxGroup legend="Select your interests" required>
 *   <Checkbox label="Technology" />
 *   <Checkbox label="Sports" />
 *   <Checkbox label="Music" />
 * </CheckboxGroup>
 */
export const CheckboxGroup = forwardRef<
  HTMLFieldSetElement,
  CheckboxGroupProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const {
    className,
    children,
    description,
    error,
    errorAriaLive: _errorAriaLive,
    hint,
    warning,
    hideLabel = false,
    legend,
    moreInfo,
    legendProps,
    onBlur,
    onFocus,
    required,
    style,
    ...rest
  } = componentProps;

  const checkboxGroupClassNames = cx(styles["checkbox-group"], className);
  const helperUid = useId();
  const moreInfoUid = useId();

  const styleCombined = {
    ...style,
    ...layoutStyles,
    alignItems:
      layoutStyles?.alignItems ??
      (layoutStyles?.flexDirection === "row" ? "flex-start" : undefined),
  };

  const labelClassNames = cx(styles["label"], {
    [styles["direction-row"]]: layoutStyles?.flexDirection === "row",
  });

  warnDeprecatedErrorUsage("CheckboxGroup", error);

  // TODO(v4.0.0): remove once legend is string only
  if (typeof legend !== "string") {
    warnOnce(
      "CheckboxGroup: Passing ReactNode to legend is deprecated. Use a plain string with inline markdown instead.",
    );
  }

  const errorMessages =
    typeof error === "boolean" || error === undefined ? undefined : error;

  const hasHelperText = hint || errorMessages || warning || description;

  const ariaDescribedBy =
    [hasHelperText && helperUid, moreInfo && moreInfoUid]
      .filter(Boolean)
      .join(" ") || undefined;

  const contextValue = useMemo(
    () => ({
      hasError: !!error,
    }),
    [error],
  );

  return (
    <fieldset
      role="group"
      data-anv="checkbox-group"
      className={checkboxGroupClassNames}
      ref={ref}
      style={styleCombined}
      onFocus={onFocus}
      onBlur={onBlur}
      {...rest}
      aria-describedby={ariaDescribedBy}
    >
      <legend className={styles["legend"]}>
        {/* TODO(v4.0.0): remove typeof check — legend will always be string */}
        {typeof legend === "string" ? stripInlineMarkdown(legend) : legend}
        {required && ", required"}
      </legend>
      {!hideLabel && (
        <div className={styles["label-row"]}>
          <FieldLabel
            aria-hidden
            required={required}
            moreInfo={moreInfo}
            moreInfoId={moreInfo ? moreInfoUid : undefined}
            {...legendProps}
            className={cx(labelClassNames, legendProps?.className)}
          >
            {legend}
          </FieldLabel>
        </div>
      )}
      <CheckboxGroupContext.Provider value={contextValue}>
        <div
          className={styles["children"]}
          style={{
            flexDirection: layoutStyles?.flexDirection,
            gap: layoutStyles?.gap,
          }}
        >
          {children}
        </div>
      </CheckboxGroupContext.Provider>
      {hasHelperText ? (
        <FieldMessage
          id={helperUid}
          hint={hint}
          error={errorMessages}
          warning={warning}
          description={description}
        />
      ) : null}
    </fieldset>
  );
});

CheckboxGroup.displayName = "CheckboxGroup";
